import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import './Register.css';

const Register = () => {
    const navigate = useNavigate();
    const { register } = useAuth();
    const { language, setLanguage } = useLanguage();

    // Simple bilingual helper — Register page is not yet in LanguageContext so we inline it
    const txt = {
        title:            language === 'vi' ? 'Đăng Ký Tài Khoản' : 'Create Account',
        fullName:         language === 'vi' ? 'Họ và Tên' : 'Full Name',
        username:         language === 'vi' ? 'Tên đăng nhập' : 'Username',
        gender:           language === 'vi' ? 'Giới tính' : 'Gender',
        male:             language === 'vi' ? 'Nam' : 'Male',
        female:           language === 'vi' ? 'Nữ' : 'Female',
        other:            language === 'vi' ? 'Khác' : 'Other',
        dob:              language === 'vi' ? 'Ngày sinh' : 'Date of Birth',
        phone:            language === 'vi' ? 'Số điện thoại' : 'Phone Number',
        email:            'Email',
        address:          language === 'vi' ? 'Địa chỉ' : 'Address',
        password:         language === 'vi' ? 'Mật khẩu' : 'Password',
        confirmPassword:  language === 'vi' ? 'Nhập lại mật khẩu' : 'Confirm Password',
        policy:           language === 'vi'
            ? 'Tôi đồng ý với tất cả điều khoản dịch vụ của ứng dụng'
            : 'I agree to all terms and conditions of the app',
        submit:           language === 'vi' ? 'Đăng Ký Ngay' : 'Register Now',
        submitting:       language === 'vi' ? 'Đang xử lý...' : 'Processing...',
        haveAccount:      language === 'vi' ? 'Đã có tài khoản?' : 'Already have an account?',
        loginLink:        language === 'vi' ? 'Đăng nhập ngay' : 'Log in',
        errPolicy:        language === 'vi'
            ? 'Vui lòng đồng ý với các điều khoản của "Phanh"'
            : 'Please agree to the terms and conditions',
        errPassword:      language === 'vi' ? 'Mật khẩu nhập lại không khớp' : 'Passwords do not match',
        errPhone:         language === 'vi' ? 'Số điện thoại không hợp lệ' : 'Invalid phone number',
        errGeneric:       language === 'vi' ? 'Lỗi hệ thống' : 'System error',
    };

    const [formData, setFormData] = useState({
        name: '',
        username: '',
        email: '',
        phone: '',
        address: '',
        dob: '',
        gender: 'female',
        password: '',
        confirmPassword: ''
    });

    const [agreedToPolicy, setAgreedToPolicy] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!agreedToPolicy) { setError(txt.errPolicy); return; }
        if (formData.password !== formData.confirmPassword) { setError(txt.errPassword); return; }
        if (formData.phone.length < 10) { setError(txt.errPhone); return; }

        setIsSubmitting(true);
        setError('');

        try {
            const result = await register({
                name: formData.name,
                username: formData.username || formData.email.split('@')[0],
                email: formData.email,
                phone: formData.phone,
                address: formData.address,
                dob: formData.dob,
                gender: formData.gender,
                password: formData.password
            });

            if (result.success) {
                navigate('/dashboard');
            } else {
                setError(result.error || (language === 'vi' ? 'Đăng ký thất bại' : 'Registration failed'));
            }
        } catch (err) {
            setError(err.message || txt.errGeneric);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="register-container">
            {/* ── Language switcher ── */}
            <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 10 }}>
                <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '10px',
                    background: 'rgba(255, 255, 255, 0.15)',
                    border: '1px solid rgba(255, 255, 255, 0.4)',
                    borderRadius: '9999px',
                    padding: '6px 20px',
                    backdropFilter: 'blur(10px)',
                    color: 'white',
                    fontWeight: '700',
                    fontSize: '1rem',
                    letterSpacing: '1px'
                }}>
                    <span
                        onClick={() => setLanguage('vi')}
                        style={{
                            cursor: 'pointer',
                            opacity: language === 'vi' ? 1 : 0.5,
                            borderBottom: language === 'vi' ? '2px solid white' : '2px solid transparent',
                            paddingBottom: '2px',
                            transition: 'all 0.2s'
                        }}>VN</span>
                    <span style={{ opacity: 0.6, fontSize: '0.9rem' }}>/</span>
                    <span
                        onClick={() => setLanguage('en')}
                        style={{
                            cursor: 'pointer',
                            opacity: language === 'en' ? 1 : 0.5,
                            borderBottom: language === 'en' ? '2px solid white' : '2px solid transparent',
                            paddingBottom: '2px',
                            transition: 'all 0.2s'
                        }}>EN</span>
                </div>
            </div>

            <div className="register-card">
                <h2 className="register-title">{txt.title}</h2>

                {error && (
                    <div className="error-alert">
                        <span>⚠️</span>
                        <span>{error}</span>
                    </div>
                )}

                <form className="register-form" onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label className="form-label">{txt.fullName}</label>
                        <input
                            type="text"
                            name="name"
                            className="form-input"
                            value={formData.name}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label className="form-label">{txt.username}</label>
                            <input
                                type="text"
                                name="username"
                                className="form-input"
                                value={formData.username}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">{txt.gender}</label>
                            <select
                                name="gender"
                                className="form-select"
                                value={formData.gender}
                                onChange={handleChange}
                            >
                                <option value="male">{txt.male}</option>
                                <option value="female">{txt.female}</option>
                                <option value="other">{txt.other}</option>
                            </select>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label className="form-label">{txt.dob}</label>
                            <input
                                type="date"
                                name="dob"
                                className="form-input"
                                value={formData.dob}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">{txt.phone}</label>
                            <input
                                type="tel"
                                name="phone"
                                className="form-input"
                                value={formData.phone}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="form-label">{txt.email}</label>
                        <input
                            type="email"
                            name="email"
                            className="form-input"
                            value={formData.email}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">{txt.address}</label>
                        <input
                            type="text"
                            name="address"
                            className="form-input"
                            value={formData.address}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label className="form-label">{txt.password}</label>
                            <input
                                type="password"
                                name="password"
                                className="form-input"
                                value={formData.password}
                                onChange={handleChange}
                                placeholder="••••••"
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">{txt.confirmPassword}</label>
                            <input
                                type="password"
                                name="confirmPassword"
                                className="form-input"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                placeholder="••••••"
                                required
                            />
                        </div>
                    </div>

                    <div className="checkbox-group">
                        <input
                            type="checkbox"
                            id="policy"
                            className="checkbox-input"
                            checked={agreedToPolicy}
                            onChange={(e) => setAgreedToPolicy(e.target.checked)}
                        />
                        <label htmlFor="policy" className="checkbox-label">
                            {language === 'vi' ? (
                                <>Tôi đồng ý với{' '}
                                    <Link to="/terms-of-service" target="_blank" style={{ color: '#667eea', textDecoration: 'underline' }}>điều khoản dịch vụ</Link>
                                    {' '}và{' '}
                                    <Link to="/privacy-policy" target="_blank" style={{ color: '#667eea', textDecoration: 'underline' }}>chính sách bảo mật</Link>.
                                </>
                            ) : (
                                <>I agree to the{' '}
                                    <Link to="/terms-of-service" target="_blank" style={{ color: '#667eea', textDecoration: 'underline' }}>terms of service</Link>
                                    {' '}and{' '}
                                    <Link to="/privacy-policy" target="_blank" style={{ color: '#667eea', textDecoration: 'underline' }}>privacy policy</Link>.
                                </>
                            )}
                        </label>
                    </div>

                    <button
                        type="submit"
                        className="register-button"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? txt.submitting : txt.submit}
                    </button>
                </form>

                <div className="login-link">
                    {txt.haveAccount}&nbsp;
                    <Link to="/login">{txt.loginLink}</Link>
                </div>
            </div>
        </div>
    );
};

export default Register;

